import { useState, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { formatCurrency, formatAxisCurrency } from "../lib/utils";
import axios from "axios";
import { API_URL } from "../lib/utils";
import { 
  TrendingUp, 
  ShoppingCart, 
  Receipt, 
  Users, 
  Clock, 
  ChefHat,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  RefreshCw
} from "lucide-react";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar
} from "recharts";
import { toast } from "sonner";
import { PaymentsTodayCard } from "../components/dashboard/PaymentsTodayCard";
import { SecurityAlertsBanner } from "../components/security/SecurityAlertsBanner";
import { FiscalReadinessBanner } from "../components/dashboard/FiscalReadinessBanner";

const DashboardPage = () => {
  const { t } = useLanguage();
  const { token, isManager } = useAuth();
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPrediction, setAiPrediction] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [statsRes, chartRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/stats`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/dashboard/sales-chart?days=7`, { headers, withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setChartData(chartRes.data);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAIPrediction = async () => {
    setAiLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/ai/sales-prediction`,
        {},
        { headers, withCredentials: true }
      );
      setAiPrediction(response.data);
      toast.success("Previsão IA gerada!");
    } catch (error) {
      toast.error("Erro ao gerar previsão IA");
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    {
      title: t("today_sales"),
      value: formatCurrency(stats?.today_sales || 0),
      icon: TrendingUp,
      trend: "+12.5%",
      trendUp: true,
      color: "text-emerald-500",
      bg: "bg-emerald-500/10",
    },
    {
      title: t("total_orders"),
      value: stats?.order_count || 0,
      icon: ShoppingCart,
      trend: "+8",
      trendUp: true,
      color: "text-blue-500",
      bg: "bg-blue-500/10",
    },
    {
      title: t("avg_ticket"),
      value: formatCurrency(stats?.average_ticket || 0),
      icon: Receipt,
      trend: "+2.3%",
      trendUp: true,
      color: "text-amber-500",
      bg: "bg-amber-500/10",
    },
    {
      title: t("tables_occupied"),
      value: `${stats?.tables_occupied || 0}/${stats?.total_tables || 0}`,
      icon: Users,
      trend: null,
      color: "text-purple-500",
      bg: "bg-purple-500/10",
    },
    {
      title: t("open_orders"),
      value: stats?.open_orders || 0,
      icon: Clock,
      trend: null,
      color: "text-rose-500",
      bg: "bg-rose-500/10",
    },
  ];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      <SecurityAlertsBanner enabled={isManager} />
      {isManager && <FiscalReadinessBanner />}
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((stat, index) => (
          <Card 
            key={index} 
            className="overflow-hidden hover:shadow-lg transition-all duration-300"
            data-testid={`stat-card-${index}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className={`p-3 rounded-xl ${stat.bg}`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                {stat.trend && (
                  <div className={`flex items-center gap-1 text-sm font-medium ${stat.trendUp ? "text-emerald-500" : "text-red-500"}`}>
                    {stat.trendUp ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                    {stat.trend}
                  </div>
                )}
              </div>
              <div className="mt-4">
                <p className="text-2xl font-bold font-['Outfit'] tracking-tight">{stat.value}</p>
                <p className="text-sm text-muted-foreground mt-1">{stat.title}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales Chart */}
        <Card className="lg:col-span-2" data-testid="sales-chart">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-['Outfit']">{t("sales_last_7_days")}</CardTitle>
            <Button variant="ghost" size="icon" onClick={fetchData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => value.slice(5)}
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickFormatter={(value) => formatAxisCurrency(value)}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                    formatter={(value) => [formatCurrency(value), "Vendas"]}
                    labelFormatter={(label) => `Data: ${label}`}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="sales" 
                    stroke="hsl(var(--primary))"
                    strokeWidth={3}
                    dot={{ fill: "hsl(var(--primary))", strokeWidth: 2 }}
                    activeDot={{ r: 6, fill: "hsl(var(--primary))" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top Products */}
        <Card data-testid="top-products">
          <CardHeader>
            <CardTitle className="font-['Outfit'] flex items-center gap-2">
              <ChefHat className="w-5 h-5 text-primary" />
              {t("top_products")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.top_products?.length > 0 ? (
                stats.top_products.map((product, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium">{product.name}</p>
                        <p className="text-sm text-muted-foreground">{product.quantity} vendidos</p>
                      </div>
                    </div>
                    <span className="font-mono font-medium text-emerald-500">
                      {formatCurrency(product.revenue)}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  Sem dados de vendas hoje
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <PaymentsTodayCard data={stats?.payments_today} />

      {/* AI Section */}
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent" data-testid="ai-section">
        <CardHeader>
          <CardTitle className="font-['Outfit'] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            {t("ai_predictions")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <Button 
              onClick={fetchAIPrediction} 
              disabled={aiLoading}
              className="gap-2"
              data-testid="generate-ai-btn"
            >
              {aiLoading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  A gerar...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  {t("sales_prediction")}
                </>
              )}
            </Button>

            {aiPrediction && (
              <div className="flex-1 p-4 rounded-lg bg-card border">
                <h4 className="font-semibold mb-2">Tendência: {aiPrediction.trend || "N/A"}</h4>
                {aiPrediction.recommendations && (
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    {aiPrediction.recommendations.slice(0, 3).map((rec, i) => (
                      <li key={i}>• {rec}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
